/**
 * PROWLER PROXIMITY ALERT
 *
 * Fair defensive warning: flags when an enemy Prowler is within 600u AND you
 * cannot currently see it (cloaked, or off-screen/timed-out). A plainly visible
 * Prowler does not trigger it — no point warning about a threat already on your
 * screen. Does NOT reveal position — that would be a wallhack. Only emits a
 * binary "danger nearby" signal, similar to stealth-game detection pings.
 *
 * Deliberately does not port SWAM's ProwlerRadar circle overlay, which reveals
 * the exact location of cloaked enemies (information the server sends trusting
 * the client to hide it).
 */

import { PLAYER_STATUS } from '@airmash/protocol';
import { uiState } from '../ui/UIState';
import { GAME_DEFAULTS } from './Config';
import { PlaneType } from './Constants';
import { getMe, forEach as playersForEach } from './Players';
import { settings } from './Settings';

/** Squared radius — avoid sqrt per player each tick. */
const PROWLER_ALERT_RADIUS_SQ = 600 * 600;

/** Poll cadence — cheap, responsive enough for an ambient warning. */
const PROWLER_ALERT_TICK_MS = 500;

let intervalId: number | null = null;
let devOverride: boolean | null = null;

/** Computes whether the local player should see a prowler proximity alert. */
function computeAlert(): boolean {
  if (!(settings.game?.prowlerAlert ?? GAME_DEFAULTS.prowlerAlert)) return false;

  const me = getMe();
  if (!me || me.status !== PLAYER_STATUS.ALIVE) return false;

  const myTeam = me.team;
  const myX = me.pos.x;
  const myY = me.pos.y;

  // Distance uses lowResPos (scoreboard every 5s, on a 128-unit grid) — the server does NOT send
  // PLAYER_UPDATE packets to enemies of a stealthed Prowler, so `pos` is never
  // refreshed for them. Scoreboard broadcasts lowX/lowY for all alive players
  // regardless of stealth, so it's our only position signal for a cloaked enemy.
  // That makes the warning up to 5 s late and the radius about 64 units soft.
  //
  // Visibility gate (matches SWAM ProwlerRadar's `render && !stealthed` test):
  // only warn about a Prowler you can't currently see. `render && !stealthed`
  // means it's on-screen, alive, fresh, and uncloaked — i.e. plainly visible —
  // so the alert would be redundant noise. Skip it. Every threatening case still
  // fires: a Prowler that cloaks on-screen sets `stealthed` (via EVENT_STEALTH,
  // which the server broadcasts to enemies too), and one approaching cloaked from
  // afar never sends us updates so it times out / culls to `render === false`.
  let nearby = false;
  playersForEach((other) => {
    if (nearby) return;
    if (other.type !== PlaneType.Prowler) return;
    if (other.team === myTeam) return;
    if (other.status !== PLAYER_STATUS.ALIVE) return;
    if (other.render && !other.stealthed) return;

    const dx = other.lowResPos.x - myX;
    const dy = other.lowResPos.y - myY;
    if (dx * dx + dy * dy < PROWLER_ALERT_RADIUS_SQ) {
      nearby = true;
    }
  });
  return nearby;
}

function tick() {
  if (devOverride !== null) return;
  const next = computeAlert();
  if (uiState.prowlerAlertActive !== next) {
    uiState.prowlerAlertActive = next;
  }
}

/**
 * Dev-only override. Pass true/false to pin the alert state (ticker stops writing);
 * pass null to resume normal proximity-driven behavior.
 */
export function setProwlerAlertDevOverride(value: boolean | null) {
  devOverride = value;
  if (value !== null) {
    uiState.prowlerAlertActive = value;
  }
}

/** Starts the periodic proximity check. Safe to call repeatedly. */
export function startProwlerAlert() {
  if (intervalId != null) return;
  intervalId = window.setInterval(tick, PROWLER_ALERT_TICK_MS);
}

/** Stops the periodic check, drops any dev override and clears any active alert. */
export function stopProwlerAlert() {
  if (intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  // A pinned override would otherwise outlive the game, with nothing on screen saying so
  devOverride = null;
  if (uiState.prowlerAlertActive) {
    uiState.prowlerAlertActive = false;
  }
}
